import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CloudOff, RefreshCw } from "lucide-react";

const KEY = "safarshala_offline_queue";
const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const write = (list) => { localStorage.setItem(KEY, JSON.stringify(list)); window.dispatchEvent(new Event("offline-queue-changed")); };

export const isNetworkError = (err) => !navigator.onLine || !err?.response;

// kind: "lead" | "lead-edit" | "booking" | "booking-edit"
export const queueAction = ({ method, url, payload, label, kind }) => {
  const list = read();
  list.push({ local_id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, method, url, payload, label, kind, queued_at: new Date().toISOString() });
  write(list);
  return list.length;
};
export const queueLead = (payload) => queueAction({ method: "post", url: "/staff/leads", payload, label: `Lead · ${payload.name}`, kind: "lead" });

// Runs an API call; if offline, queues it and returns { queued: true }
export async function offlineAware(fn, queueSpec) {
  try { return await fn(); }
  catch (err) {
    if (!isNetworkError(err)) throw err;
    const n = queueAction(queueSpec);
    toast.success(`No signal — "${queueSpec.label}" saved on this device (${n} waiting). It will sync automatically.`, { duration: 5000 });
    return { queued: true };
  }
}

let syncing = false;
export async function syncOfflineQueue() {
  if (syncing || !navigator.onLine) return 0;
  const list = read();
  if (!list.length) return 0;
  syncing = true;
  let done = 0;
  try {
    for (const item of list) {
      try {
        await api[item.method](item.url, item.payload);
        write(read().filter((x) => x.local_id !== item.local_id));
        done++;
      } catch (err) {
        if (!err.response) break; // still offline — keep order, retry later
        write(read().filter((x) => x.local_id !== item.local_id));
        toast.error(`"${item.label}" was rejected by the server: ${err.response?.data?.detail?.toString?.().slice(0, 80) || err.response.status}`, { duration: 7000 });
      }
    }
  } finally { syncing = false; }
  if (done) { toast.success(`${done} offline change${done > 1 ? "s" : ""} synced`); window.dispatchEvent(new Event("leads-synced")); window.dispatchEvent(new Event("offline-synced")); }
  return done;
}
export const syncOfflineLeads = syncOfflineQueue;

export function useOfflineQueue() {
  const [items, setItems] = useState(read());
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const upd = () => setItems(read());
    const on = () => { setOnline(true); syncOfflineQueue(); };
    const off = () => setOnline(false);
    window.addEventListener("offline-queue-changed", upd); window.addEventListener("online", on); window.addEventListener("offline", off);
    syncOfflineQueue();
    return () => { window.removeEventListener("offline-queue-changed", upd); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return { count: items.length, items, online };
}

export function useOfflineSynced(cb) {
  useEffect(() => { window.addEventListener("offline-synced", cb); return () => window.removeEventListener("offline-synced", cb); }, [cb]);
}

export function OfflineBanner() {
  const { count, items, online } = useOfflineQueue();
  if (online && !count) return null;
  const summary = items.slice(0, 3).map((i) => i.label).join(" · ") + (items.length > 3 ? ` · +${items.length - 3} more` : "");
  return (
    <div className={`flex items-center gap-2 px-4 py-1.5 text-xs font-medium ${online ? "bg-amber-500/15 text-amber-800 dark:text-amber-300" : "bg-slate-800 text-white"}`} data-testid="offline-banner">
      {online ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CloudOff className="h-3.5 w-3.5" />}
      <span className="truncate">{online ? `Syncing ${count} offline change${count > 1 ? "s" : ""}… ${summary}` : `You're offline — leads, bookings and follow-ups you save are kept on this device${count ? ` (${count} waiting: ${summary})` : ""}`}</span>
      {online && <button type="button" className="ml-auto underline shrink-0" onClick={syncOfflineQueue} data-testid="offline-sync-now">Sync now</button>}
    </div>
  );
}
